import * as React from "react";
import { ChartData, ChartUnit, formatXLabelSpreadsheet, GroupBy, OrderedArray, PageView, TimeStep } from "./utils";
import { Loader } from "../../components/layout/loader";
import { VariableSizeGrid } from "react-window";
import textWidth from "text-width";

interface Props {
  isLoading: boolean;
  chartData: ChartData;
  pageView: PageView;
  groupBy: GroupBy;
  timeStep: TimeStep;
  shouldAddVolumesetCost: boolean;
  isBillingDashboard: boolean;
}
export const UsageSpreadsheetView: React.FC<Props> = ({
  isLoading,
  chartData,
  pageView,
  groupBy,
  timeStep,
  shouldAddVolumesetCost,
  isBillingDashboard,
}) => {
  if (isLoading) {
    return <Loader reason="querying usage in grid view" />;
  }

  const cdus: ChartUnit[] = React.useMemo(() => {
    const datakeys: string[] = [];
    const cdus: ChartUnit[] = [];
    for (const graphData of chartData.graphData) {
      let arr: OrderedArray = [];
      if (pageView === "cost") {
        if (groupBy === "workloads" && shouldAddVolumesetCost) {
          arr = graphData.secondaryTotalMapOrderedRaw;
        } else {
          arr = graphData.primaryTotalMapOrderedRaw;
        }
      } else {
        arr = graphData.valueMapOrderedRaw;
      }
      for (const arrItem of arr) {
        const dataKey = arrItem[1];
        if (datakeys.includes(dataKey)) {
          continue;
        }
        const cdu = chartData.units.find((cdu) => cdu.dataKey === dataKey);
        if (!cdu) {
          continue;
        }

        datakeys.push(dataKey);
        cdus.push(cdu);
      }
    }
    return cdus;
  }, [isLoading, chartData, pageView, groupBy, shouldAddVolumesetCost]);

  const hasGvc = React.useMemo(() => {
    if (groupBy === "gvc") {
      return false;
    }
    return chartData.units.some((cdu) => !!cdu.gvc);
  }, [chartData]);
  const hasOrg = React.useMemo(() => {
    if (!isBillingDashboard) {
      return false;
    }
    if (groupBy === "org") {
      return false;
    }
    if (groupBy === "observability") {
      return false;
    }
    return chartData.units.some((cdu) => !!cdu.org);
  }, [chartData]);

  const data: any[][] = React.useMemo(() => {
    const data: any[][] = [];
    const firstRow: string[] = [];
    firstRow.push("Item Name");
    if (hasOrg) {
      firstRow.push("Org Name");
    }
    if (hasGvc) {
      firstRow.push("Gvc Name");
    }
    firstRow.push(...chartData.graphData.map((gd) => formatXLabelSpreadsheet(gd.periodTimes, timeStep)));
    data.push(firstRow);

    for (const cdu of cdus) {
      const itemArray: any[] = [cdu.label || ""];
      if (hasOrg) {
        itemArray.push(cdu.org || "");
      }
      if (hasGvc) {
        itemArray.push(cdu.gvc || "");
      }

      for (const gd of chartData.graphData) {
        let arr: OrderedArray = [];
        if (pageView === "cost") {
          if (groupBy === "workloads" && shouldAddVolumesetCost) {
            arr = gd.secondaryTotalMapOrderedRaw;
          } else {
            arr = gd.primaryTotalMapOrderedRaw;
          }
        } else {
          arr = gd.valueMapOrderedRaw;
        }
        const item = arr.find((arrItem) => arrItem[1] === cdu.dataKey);
        let value = 0;
        if (item && item[0] !== undefined && item[0] !== undefined) {
          value = item[0] || 0;
        }
        value = Number(value.toFixed(2));
        itemArray.push(value);
      }
      data.push(itemArray);
    }
    return data;
  }, [cdus, hasOrg, hasGvc]);

  const columnWidths: number[] = React.useMemo(() => {
    const DEFAULT_COLUMN_WIDTH = 50;
    const columnWidths: number[] = [];
    const columnCount = data[0].length;
    const rowCount = data.length;
    const columnContents: string[][] = [];
    for (let index = 0; index < columnCount; index++) {
      columnContents[index] = new Array(rowCount).fill("");
      columnWidths[index] = DEFAULT_COLUMN_WIDTH;
    }
    for (let index = 0; index < columnCount; index++) {
      for (const rowIndexStr in data) {
        const rowIndex = Number(rowIndexStr);
        const row = data[rowIndex];
        const columnContent = row[index];
        columnContents[index][rowIndex] = columnContent;
      }
    }

    for (const columnContentsIndexStr in columnContents) {
      const columnContentsIndex = Number(columnContentsIndexStr);
      try {
        const columnContentArray = columnContents[columnContentsIndex];
        let size = DEFAULT_COLUMN_WIDTH;
        for (const columnContent of columnContentArray) {
          const width = textWidth(columnContent, { font: "12px proxima-nova" });
          if (width > size) {
            size = width;
          }
        }
        columnWidths[columnContentsIndex] = size;
      } catch (e) {}
    }
    return columnWidths;
  }, [data]);

  function getCellContent(data: any[], rowIndex: number, columnIndex: number) {
    const item = data[rowIndex];
    if (!item) {
      return "nodata-row";
    }
    const content = item[columnIndex];
    if (content === undefined) {
      return "nodata-column";
    }
    return content;
  }

  return (
    <div className="mb-6">
      <div className="text-xs border rounded" style={{ borderColor: "rgba(0, 0, 0, 0.15)", width: 1370 }}>
        <VariableSizeGrid
          itemData={data}
          width={1370}
          height={600}
          rowCount={data.length}
          columnCount={data[0].length}
          columnWidth={(index) => {
            return columnWidths[index] + 6;
          }}
          rowHeight={(index) => 30}
        >
          {({ data, style, rowIndex, columnIndex }) => {
            return (
              <div
                style={{
                  backgroundColor: rowIndex % 2 === 0 ? "rgba(0, 0, 0, 0.05)" : "rgba(0, 0, 0, 0.025)",
                  ...style,
                }}
                className={`px-2 flex items-center ${rowIndex === 0 ? "font-semibold" : ""}`}
              >
                <span>{getCellContent(data, rowIndex, columnIndex)}</span>
              </div>
            );
          }}
        </VariableSizeGrid>
      </div>
    </div>
  );
};
